/**
 * PATH: packages/email-hub/src/feature/components/IntelligenceAnalysis.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-INTEL-ANALYSIS-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/AIEmailPanel.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/AIEmailPanel.tsx:
 *     thin re-export shim that points back here.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Loader2,
  MapPin,
  MessageSquare,
  Phone,
  RefreshCw,
  Sparkles,
  TrendingUp,
  User,
  Zap,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useEmailClassify, getCachedClassification } from '../hooks/useEmailAI'
import type { Email } from '../hooks/useEmails'
import { getCategoryDisplay } from '../lib/utils'
import WhyPanel from './WhyPanel'

const ENTITY_ICONS: Record<string, typeof User> = {
  job_ref: Briefcase,
  client: User,
  person: User,
  company: User,
  amount: DollarSign,
  date: Calendar,
  address: MapPin,
  phone: Phone,
}

const URGENCY_STYLES: Record<string, string> = {
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

const SENTIMENT_LABELS: Record<string, { label: string; color: string }> = {
  positive: { label: 'Positive', color: 'text-emerald-600' },
  neutral: { label: 'Neutral', color: 'text-slate-500' },
  negative: { label: 'Negative', color: 'text-amber-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
}

function ConfidenceMeter({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) {
  const color = value >= 80 ? 'bg-emerald-500' : value >= 60 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className={cn('flex items-center gap-1.5', size === 'md' && 'gap-2')}>
      <div className={cn('rounded-full bg-muted overflow-hidden', size === 'sm' ? 'h-1.5 w-16' : 'h-2 w-24')}>
        <div className={cn('h-full rounded-full transition-all duration-500', color)} style={{ width: `${value}%` }} />
      </div>
      <span className={cn('font-medium', size === 'sm' ? 'text-[10px]' : 'text-xs')}>{value}%</span>
    </div>
  )
}

interface IntelligenceAnalysisProps {
  email: Email
  compact?: boolean
  autoAnalyze?: boolean
  onActionClick?: (action: string, payload?: Record<string, unknown>) => void
}

export default function IntelligenceAnalysis({ email, compact = false, autoAnalyze = false, onActionClick }: IntelligenceAnalysisProps) {
  const { result, loading, pending, error, classify, focus } = useEmailClassify()
  // Operator 2026-06-13 ("by default have all sections expanded — you
  // don't want to click when scrolling down"). The compact (rail-
  // embedded) instance used to start collapsed; default to expanded so
  // the classification/details show without a click. The header toggle
  // (compact only) still collapses it on demand.
  const [expanded, setExpanded] = useState(true)

  const data = result || getCachedClassification(email.id)

  // BUG-2026-06-23-EMAIL-AI-PANEL-STALE — when the reader switches emails,
  // drop the previous email's in-memory result so it can't render under the
  // new header (the cache for THIS email, if any, still shows via `data`).
  // `focus` also re-targets the hook's stale-response guard at this email.
  useEffect(() => {
    focus(email.id)
  }, [email.id, focus])

  useEffect(() => {
    // Auto-trigger on open / switch. The classifier itself falls back to
    // body_preview / HTML when body_text is absent, so gating the trigger on
    // ``body_text`` alone meant HTML-only marketing emails (no plain-text
    // part) never auto-analysed. Gate on the CACHE (not the live `data`,
    // which holds the just-cleared previous result for one render after a
    // switch) so a newly-selected, never-analysed email still auto-runs.
    const hasContent = !!(email.body_text || email.body_preview || email.body_html)
    if (autoAnalyze && !getCachedClassification(email.id) && hasContent) {
      classify(email)
    }
  }, [autoAnalyze, email.id, classify])

  if (compact && !data && !loading) {
    return (
      <button
        onClick={() => classify(email)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-gradient-to-r from-violet-500/10 to-purple-500/10 text-violet-700 dark:text-violet-300 rounded-lg hover:from-violet-500/20 hover:to-purple-500/20 transition-all border border-violet-200/50 dark:border-violet-800/50"
      >
        <Sparkles className="h-3 w-3" />
        AI Analyze
      </button>
    )
  }

  return (
    <div className={cn(
      'rounded-xl border transition-all duration-200',
      data
        ? 'bg-gradient-to-br from-violet-50/80 to-purple-50/50 dark:from-violet-950/30 dark:to-purple-950/20 border-violet-200/60 dark:border-violet-800/40'
        : 'bg-muted/30 border-border',
    )}>
      <div
        className={cn('flex items-center justify-between p-3', compact && 'cursor-pointer')}
        onClick={compact ? () => setExpanded(!expanded) : undefined}
      >
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          AI Analysis
        </h3>
        <div className="flex items-center gap-2">
          {data && <ConfidenceMeter value={data.confidence} />}
          <button
            onClick={(e) => { e.stopPropagation(); classify(email, true) }}
            disabled={loading}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
            title="Re-analyze"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="px-3 pb-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
          {/* Usually a stored-analysis read (analysis persists in the DB at
              email sync); the server only runs DeepSeek when no row exists
              or on explicit re-analyze — so don't claim "Analyzing". */}
          Loading AI analysis…
        </div>
      )}

      {/* BUG-2026-07-17-EMAIL-AI-PENDING-CACHED — no stored run yet: the
          server enqueued one and the hook is polling. Say so instead of
          rendering an empty "0% / no entities" analysis. */}
      {pending && !loading && !data && (
        <div className="px-3 pb-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
          Analysing this email — the result will appear here shortly…
        </div>
      )}

      {error && !data && (
        <div className="px-3 pb-3 text-sm text-destructive">{error}</div>
      )}

      {data && (!compact || expanded) && (
        <div className="px-3 pb-3 space-y-3">
          {/* Category + Urgency */}
          <div className="flex items-center gap-2 flex-wrap">
            {(() => {
              const cat = getCategoryDisplay(data.category)
              return (
                <span className={cn('px-2.5 py-1 text-xs font-medium rounded-full flex items-center gap-1', cat.color)}>
                  <FileText className="h-3 w-3" />
                  {data.category_label}
                </span>
              )
            })()}
            {data.urgency && data.urgency !== 'normal' && (
              <span className={cn('px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full', URGENCY_STYLES[data.urgency] || URGENCY_STYLES.normal)}>
                <Zap className="h-2.5 w-2.5 inline mr-0.5" />
                {data.urgency}
              </span>
            )}
            {data.sentiment && (
              <span className={cn('text-[10px] font-medium', SENTIMENT_LABELS[data.sentiment]?.color || 'text-slate-500')}>
                {SENTIMENT_LABELS[data.sentiment]?.label || data.sentiment}
              </span>
            )}
          </div>

          {/* Summary */}
          <p className="text-sm text-foreground/90 leading-relaxed">{data.summary}</p>

          {/* Entities */}
          {data.entities.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Extracted Entities</p>
              <div className="space-y-1">
                {data.entities.map((ent, i) => {
                  const Icon = ENTITY_ICONS[ent.entity_type] || FileText
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground font-medium">{ent.value}</span>
                      {ent.label && <span className="text-muted-foreground text-xs">({ent.label})</span>}
                      {ent.job_id && (
                        <Link to={`/jobs/${ent.job_id}`} className="text-[10px] text-primary hover:underline">
                          View Job
                        </Link>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Key Dates & Amounts */}
          {(data.key_dates.length > 0 || data.key_amounts.length > 0) && (
            <div className="flex gap-4">
              {data.key_dates.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Dates</p>
                  <div className="flex flex-wrap gap-1">
                    {data.key_dates.map((d, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.key_amounts.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Amounts</p>
                  <div className="flex flex-wrap gap-1">
                    {data.key_amounts.map((a, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Suggested Actions */}
          {data.suggested_actions.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Suggested Actions</p>
              <div className="space-y-1.5">
                {data.suggested_actions.slice(0, 4).map((action, i) => (
                  <button
                    key={i}
                    onClick={() => onActionClick?.(action.action, action.payload)}
                    className="w-full flex items-center justify-between p-2 bg-background/80 rounded-lg border border-border hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-sm transition-all text-left group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-1 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 group-hover:bg-violet-200 dark:group-hover:bg-violet-900/50 transition-colors">
                        <TrendingUp className="h-3 w-3" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-foreground">{action.label}</span>
                        {action.description && (
                          <p className="text-[10px] text-muted-foreground truncate">{action.description}</p>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-violet-500 transition-colors shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Reply */}
          {data.suggested_reply && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                <MessageSquare className="h-3 w-3 inline mr-1" />
                Suggested Reply
              </p>
              <div className="p-2.5 bg-background/80 rounded-lg border border-border text-sm text-foreground/80 italic leading-relaxed">
                "{data.suggested_reply}"
              </div>
            </div>
          )}

          {/* Provenance — why this classification/links (lazy, Phase 4) */}
          <WhyPanel emailId={email.id} />
        </div>
      )}
    </div>
  )
}
